import { CalendarIcon, CarIcon, CheckIcon, ClockIcon } from '../components/icons'
import { formatMoney, formatSlotDate, formatSlotTime } from '../lib/format'
import type { BasketItem } from '../lib/services'
import type { PaymentPlan } from '../lib/payment'
import type {
  AvailableJobTimeSlotDTO,
  WidgetBookingStatus,
} from '../types/PublicBookingChannelTypes'

export interface ConfirmationStepProps {
  reference: string
  /**
   * From the server. REQUESTED means the garage still has to accept, so the
   * screen must not say "booked in". Null from an older backend.
   */
  status?: WidgetBookingStatus | null
  /** Chosen tyre, which is not a basket item — see BookingWidget. */
  tyreLine?: { description: string; total: number } | null
  businessName: string
  slot: AvailableJobTimeSlotDTO | null
  basket: BasketItem[]
  showPrices: boolean
  plan: PaymentPlan
  vehicleLabel: string | null
  contactPhone?: string | null
  contactAddress?: string | null
  calendarHref: string | null
  onBookAnother: () => void
}

export function ConfirmationStep({
  reference,
  status = null,
  tyreLine,
  businessName,
  slot,
  basket,
  showPrices,
  plan,
  vehicleLabel,
  contactPhone,
  contactAddress,
  calendarHref,
  onBookAnother,
}: ConfirmationStepProps) {
  const requested = status === 'REQUESTED'
  const garage = businessName.trim() || 'The garage'
  const paidNow =
    plan.mode !== 'none' && plan.amountDue != null && plan.amountDue > 0 ? plan.amountDue : null

  return (
    <div className="ac-confirmation">
      <div className="ac-confirmation__badge" aria-hidden="true">
        {requested ? <ClockIcon size={30} /> : <CheckIcon size={30} />}
      </div>

      <div className="ac-stack ac-stack--tight">
        <h3 className="ac-heading ac-heading--display">
          {requested ? 'Request sent' : "You're booked in"}
        </h3>
        <p className="ac-prose">
          {requested
            ? `${paidNow != null ? `You've paid ${formatMoney(paidNow)}. ` : ''}${garage} will confirm by email.`
            : `We've emailed your confirmation. ${garage} will be in touch if anything needs to change.`}
        </p>
      </div>

      {reference ? (
        <div>
          <p className="ac-hint">Your reference</p>
          <p className="ac-reference">{reference}</p>
        </div>
      ) : null}

      <div className="ac-summary ac-summary--full">
        {slot ? (
          <>
            <div className="ac-summary__row">
              <span>
                <CalendarIcon size={16} /> Date
              </span>
              <span>{formatSlotDate(slot.startTime)}</span>
            </div>
            <div className="ac-summary__row">
              <span>
                <ClockIcon size={16} /> Drop off
              </span>
              <span>{formatSlotTime(slot.startTime)}</span>
            </div>
          </>
        ) : null}

        {vehicleLabel ? (
          <div className="ac-summary__row">
            <span>
              <CarIcon size={16} /> Vehicle
            </span>
            <span>{vehicleLabel}</span>
          </div>
        ) : null}

        {basket.map((item) => (
          <div key={item.serviceId} className="ac-summary__row">
            <span>{item.name}</span>
            {showPrices && item.included ? <span>Included</span> : null}
            {showPrices && !item.included && item.price != null ? (
              <span>{formatMoney(item.price)}</span>
            ) : null}
          </div>
        ))}

        {tyreLine ? (
          <div className="ac-summary__row">
            <span>{tyreLine.description}</span>
            {showPrices ? <span>{formatMoney(tyreLine.total)}</span> : null}
          </div>
        ) : null}

        {showPrices && plan.total > 0 ? (
          <div className="ac-summary__row ac-summary__row--total">
            <span>Total</span>
            <span>{formatMoney(plan.total)}</span>
          </div>
        ) : null}

        {plan.mode === 'deposit' && plan.amountDue != null ? (
          <div className="ac-summary__row">
            <span>Deposit paid</span>
            <span>{formatMoney(plan.amountDue)}</span>
          </div>
        ) : null}

        {plan.mode === 'full' && plan.amountDue != null ? (
          <div className="ac-summary__row">
            <span>Paid</span>
            <span>{formatMoney(plan.amountDue)}</span>
          </div>
        ) : null}

        {plan.mode === 'none' && plan.total > 0 && showPrices ? (
          <div className="ac-summary__row">
            <span>To pay at the garage</span>
            <span>{formatMoney(plan.total)}</span>
          </div>
        ) : null}
      </div>

      {contactAddress || contactPhone ? (
        <div className="ac-stack ac-stack--tight">
          {contactAddress ? <p className="ac-hint">{contactAddress}</p> : null}
          {contactPhone ? (
            <p className="ac-hint">
              <a href={`tel:${contactPhone.replace(/\s+/g, '')}`}>{contactPhone}</a>
            </p>
          ) : null}
        </div>
      ) : null}

      {calendarHref ? (
        <a className="ac-btn ac-btn--secondary ac-btn--link" href={calendarHref} download="booking.ics">
          <CalendarIcon size={18} /> Add to calendar
        </a>
      ) : null}

      <button type="button" className="ac-btn ac-btn--ghost" onClick={onBookAnother}>
        Make another booking
      </button>
    </div>
  )
}
